/**
 * `/trainers/:id/today` — one trainer's clients for the day, with the context
 * they need walking in: lifecycle + remaining credits (low-credit flag), member
 * memory (summary + long-term), recent session notes, active marketing flows,
 * and the consultation profile. Topped with the shared DayContextHeader.
 * Sourced from `GET /api/trainers/:id/today` (one aggregate fetch); "today" is
 * resolved in the studio timezone server-side.
 */

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  api,
  ApiError,
  type DailyBriefClient,
  type DailyBriefMessage,
  type TodayBoardResponse,
  type TrainerTodayResponse,
} from "../lib/api";
import { CollapsibleJson } from "../components/json-tree";
import { DayContextHeader } from "../components/DayContextHeader";
import { studioFormat } from "../lib/studio-tz";

const DATE_OPTS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "2-digit",
};

const DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
};

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : studioFormat(DATE_OPTS).format(d);
}

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : studioFormat(DATETIME_OPTS).format(d);
}

function LifecycleTag({ value }: { readonly value: string }): JSX.Element {
  return (
    <span className={`tag ${value === "customer" ? "tag-success" : ""}`}>
      {value}
    </span>
  );
}

/**
 * Trainer switcher — lets an admin page through every trainer's day from this
 * single screen (dropdown + ‹/› step). Options are the trainers with a window
 * today (from `GET /api/today`); the current trainer is always present even if
 * they have no sessions. Convenience chrome: if the roster won't load we just
 * hide it rather than block the brief.
 */
function TrainerSwitcher({
  currentId,
  currentName,
}: {
  readonly currentId: string;
  readonly currentName: string;
}): JSX.Element | null {
  const navigate = useNavigate();
  const [roster, setRoster] = useState<TodayBoardResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .todayBoard()
      .then((r) => {
        if (!cancelled) setRoster(r);
      })
      .catch(() => {
        // Convenience chrome — stay silent if the roster won't load.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fromBoard = (roster?.trainers ?? []).map((t) => ({
    id: t.trainer_id,
    label: `${t.display_name ?? "Trainer"} · ${t.session_count} session${
      t.session_count === 1 ? "" : "s"
    }`,
  }));
  const options = fromBoard.some((o) => o.id === currentId)
    ? fromBoard
    : [{ id: currentId, label: `${currentName} · 0 sessions` }, ...fromBoard];

  const idx = options.findIndex((o) => o.id === currentId);
  const prev = idx > 0 ? options[idx - 1] : undefined;
  const next =
    idx >= 0 && idx < options.length - 1 ? options[idx + 1] : undefined;
  const go = (id: string): void => {
    navigate(`/trainers/${encodeURIComponent(id)}/today`);
  };

  return (
    <div className="trainer-switcher">
      <span className="trainer-switcher-label">Viewing trainer</span>
      <button
        type="button"
        className="switcher-arrow"
        disabled={prev === undefined}
        onClick={() => {
          if (prev !== undefined) go(prev.id);
        }}
        aria-label="Previous trainer"
      >
        ‹
      </button>
      <select
        className="switcher-select"
        value={currentId}
        onChange={(e) => go(e.target.value)}
        aria-label="Switch trainer"
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="switcher-arrow"
        disabled={next === undefined}
        onClick={() => {
          if (next !== undefined) go(next.id);
        }}
        aria-label="Next trainer"
      >
        ›
      </button>
    </div>
  );
}

function ClientCard({ c }: { readonly c: DailyBriefClient }): JSX.Element {
  return (
    <div className="client-card">
      <div className="client-card-head">
        <span className="client-time">
          {c.starts_at_local}–{c.ends_at_local}
        </span>
        <Link
          to={`/members/${encodeURIComponent(c.member_id)}`}
          className="data-member-link client-name"
        >
          {c.member_name ?? "Member"}
        </Link>
        <LifecycleTag value={c.lifecycle} />
        {c.session_type !== null ? (
          <span className="dc-muted">{c.session_type}</span>
        ) : null}
        {c.location !== null ? (
          <span className="dc-muted">@ {c.location}</span>
        ) : null}
        {c.status === "completed" ? (
          <span className="tag tag-success">done</span>
        ) : null}
        <span className="client-credits">
          {c.remaining_credits} credit{c.remaining_credits === 1 ? "" : "s"}
          {c.low_on_sessions ? (
            <span className="tag tag-error"> ⚠ low</span>
          ) : null}
        </span>
      </div>

      {c.active_flows.length > 0 ? (
        <div className="client-row">
          <span className="client-label">Flows</span>
          <span>
            {c.active_flows.map((f, i) => (
              <span className="tag" key={`${f.flow_name}-${i}`}>
                {f.flow_name} · step {f.current_step_index}
              </span>
            ))}
          </span>
        </div>
      ) : null}

      {c.memory !== null &&
      (c.memory.summary_md !== null || c.memory.longterm_md !== null) ? (
        <details className="client-memory">
          <summary>Member memory</summary>
          {c.memory.summary_md !== null ? (
            <div className="trainer-memory-md">{c.memory.summary_md}</div>
          ) : null}
          {c.memory.longterm_md !== null ? (
            <div className="trainer-memory-md">{c.memory.longterm_md}</div>
          ) : null}
        </details>
      ) : null}

      {c.recent_notes.length > 0 ? (
        <div className="client-row">
          <span className="client-label">Recent notes</span>
          <span className="client-notes">
            {c.recent_notes.map((n, i) => (
              <div className="client-note" key={i}>
                <span className="dc-muted">{fmtDate(n.created_at)}</span>{" "}
                {n.body}
              </div>
            ))}
          </span>
        </div>
      ) : null}

      {c.consultation_profile !== null ? (
        <details className="client-memory">
          <summary>Consultation profile</summary>
          <CollapsibleJson value={c.consultation_profile} />
        </details>
      ) : null}

      {c.onboarding_profile !== null ? (
        <details className="client-memory">
          <summary>Onboarding answers</summary>
          <CollapsibleJson value={c.onboarding_profile} />
        </details>
      ) : null}
    </div>
  );
}

/**
 * Communication panel — the AI ↔ trainer WhatsApp thread, mirrored from the
 * member-detail thread on `/members/:id` (reuses `.member-chat`/`.member-msg*`
 * CSS). `out` = AI/Layer messaged the trainer (Morning Brief, post-session
 * capture…), `in` = the trainer replied. Oldest-first top-to-bottom.
 */
function TrainerThread({
  trainerFirstName,
  hasPhone,
  messages,
}: {
  readonly trainerFirstName: string;
  readonly hasPhone: boolean;
  readonly messages: ReadonlyArray<DailyBriefMessage>;
}): JSX.Element {
  return (
    <section className="panel">
      <h2>Communication · AI ↔ {trainerFirstName}</h2>
      {!hasPhone ? (
        <p className="empty">
          No WhatsApp number on file for this trainer — the AI can&apos;t
          message them yet.
        </p>
      ) : messages.length === 0 ? (
        <p className="empty">No messages with the AI yet.</p>
      ) : (
        <div className="member-chat">
          {messages
            .slice()
            .reverse()
            .map((msg, i) => (
              <div
                key={`${msg.ts}:${i}`}
                className={`member-msg member-msg-${msg.dir}`}
              >
                <div className="member-msg-meta">
                  {msg.dir === "in" ? trainerFirstName : "AI"} ·{" "}
                  {fmtDateTime(msg.ts)}
                  {msg.dir === "out" &&
                  msg.status !== null &&
                  msg.status !== "sent" ? (
                    <span
                      className={`tag ${
                        msg.status === "failed" ? "tag-error" : ""
                      }`}
                    >
                      {" "}
                      {msg.status}
                    </span>
                  ) : null}
                </div>
                <div className="member-msg-body">{msg.body ?? "(no text)"}</div>
              </div>
            ))}
        </div>
      )}
    </section>
  );
}

export function TrainerToday(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<TrainerTodayResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (id === undefined) {
      setError("missing_trainer_id");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .trainerToday(id)
      .then((r) => {
        if (cancelled) return;
        setData(r);
        setError(null);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(
          e instanceof ApiError ? e.code ?? `http_${e.status}` : e.message,
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) return <p className="empty">Loading…</p>;
  if (error !== null) {
    const notFound = error === "trainer_not_found";
    return (
      <div>
        <p className="member-breadcrumb">
          <Link to="/calendar?view=today" className="data-member-link">
            ← Today
          </Link>
        </p>
        <p className="empty">
          {notFound
            ? "Trainer not found in this studio."
            : `Couldn't load this brief (${error}).`}
        </p>
      </div>
    );
  }
  if (data === null) return <p className="empty">Not found.</p>;

  const t = data.trainer;
  return (
    <div>
      <p className="member-breadcrumb">
        <Link to="/calendar?view=today" className="data-member-link">
          ← Today
        </Link>
        {"  ·  "}
        <Link
          to={`/trainers/${encodeURIComponent(t.id)}`}
          className="data-member-link"
        >
          {t.display_name}&apos;s profile
        </Link>
      </p>
      <TrainerSwitcher currentId={t.id} currentName={t.display_name} />
      <h1 className="page-title member-title">
        {t.display_name} · {data.briefing_date_label}
      </h1>
      <DayContextHeader />

      <section className="panel">
        <h2>Clients today ({data.clients_today.length})</h2>
        {data.clients_today.length === 0 ? (
          <p className="empty">No sessions booked for this trainer today.</p>
        ) : (
          data.clients_today.map((c) => (
            <ClientCard key={c.booking_id} c={c} />
          ))
        )}
      </section>

      <TrainerThread
        trainerFirstName={t.display_name.split(" ")[0] ?? t.display_name}
        hasPhone={t.phone !== null}
        messages={data.messages}
      />
    </div>
  );
}
